import {
  AfterViewInit,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  inject,
  Input,
  OnDestroy,
  ViewChild
} from '@angular/core';
import {
  ICompassWidgetPermissions
} from '../../shared/interfaces/widget-permissions/widgetPermission.interface';
import {
  IAppDashboard,
  INgsiSubscriptionWidget,
  IPermissionsFlags,
  IPluginPermissions,
  IUser,
  ModelService,
	PluginInjectorWidget,
	PluginInjectorService
} from 'core-client/api';
import {
  IObcNavigationInstruments
} from '../../shared/interfaces/oicl-obenbridge-interfaces/obc-navigations-instrument.interface';
import { INotification, NgsildConnectorService } from 'ngsild-connector';
import { filter, Subject, takeUntil } from 'rxjs';
import { INgsiEntity, INgsiEntityIdAttrs } from '../../shared/interfaces/ngsi/entity.interface';
import { IOiclOpenBridgeCompassWidget } from '../../shared/interfaces/compass/oicl-openbridge-compass-widget.interface';
import * as ModuleEntityTypesConfig from '../../shared/config/module.ngsi-entity-types.constants';
import { INgsiAIS } from '../../shared/interfaces/ngsi/ais-interface';
import { CompassService } from '../services/compass.service';

import '@oicl/openbridge-webcomponents/dist/navigation-instruments/compass/compass.js';

@Component({
  selector: 'lib-compass-view',
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './compass-view.component.html',
  styleUrl: './compass-view.component.scss'
})
export class CompassViewComponent implements AfterViewInit, OnDestroy {
  // Services
  readonly ngsildConnectorService: NgsildConnectorService = inject(NgsildConnectorService);
  readonly pluginInjectorService: PluginInjectorService = inject(PluginInjectorService);
  readonly modelService: ModelService = inject(ModelService);
  readonly compassService: CompassService = inject(CompassService);

  // Variables
  protected obcNavigationsInstrumentsInstance: Required<Pick<IObcNavigationInstruments, 'compass'>> = {
    compass: {
      heading: 0,
      courseOverGround: 0,
      headingAdvices: [{
        minAngle: 20,
        maxAngle: 50,
        type: 'advice',
        hinted: false
      }]
    }
  };

  @ViewChild('obcNavigationInstrumentContainer',{ static: false })
  protected obcNavigationInstrumentContainer!: ElementRef;

  @Input()
  data: PluginInjectorWidget.IWidget | undefined;

  @Input()
  isHeaderHovered: boolean = false;

  private readonly ngUnsubscribe$: Subject<void> = new Subject<void>();
  private widgetSubscriptions: INgsiSubscriptionWidget[] = [];
  private resizeObserver: ResizeObserver | undefined;

  protected openBridgeCompassWidget: IOiclOpenBridgeCompassWidget | undefined;
  protected widgetId: string | undefined;
  protected currDashboardId: string | undefined;
  protected ngsiEntityId: Omit<INgsiEntityIdAttrs, 'ngsildPrefix'> | undefined;
  protected entityType: string = ModuleEntityTypesConfig.NGSI_ENTITY_TYPES.compass.entityTypes[0].AIS;

  // Permission of other plugins
  protected extraPermissionRefs: { ngsildConnectorEnabled: string } = {
    ngsildConnectorEnabled: 'authorizeNgsildConnector'
  };

  // WidgetPermissions
  protected widgetPermissions: ICompassWidgetPermissions = {
    widgetEnabled: false,
    widgetSettingsEditEnabled: false,
    ngsildConnectorEnabled: false,
  };

  async ngAfterViewInit(): Promise<void> {
    if (!this.data?.appPluginWidget) {
      console.error(`Widget data not correctly loaded.`);
      return;
    }

    // Observe changes such as onHover for widget headers
    this.observeStyleChanges();

    this.widgetSubscriptions = await this.modelService._ngsiSubscriptionWidget.listAll(`type=${this.entityType}`);

    await this.getWidgetAccessRights(
        this.modelService._user.getUser(),
        this.pluginInjectorService,
        this.data
    );

    const user = this.modelService._user.getUser();
    if (!user?._id) {
      console.error(`[Compass Widget] User not found.`);
      return;
    }

    this.currDashboardId = user.active_dashboard;

    this.openBridgeCompassWidget = this.data.appPluginWidget;
    this.widgetId = this.openBridgeCompassWidget?._id;
    this.ngsiEntityId = this.openBridgeCompassWidget?.settings?.entityId;
    this.obcNavigationsInstrumentsInstance.compass.heading = this.openBridgeCompassWidget?.settings?.heading ?? 0;
    this.obcNavigationsInstrumentsInstance.compass.courseOverGround = this.openBridgeCompassWidget?.settings?.courseOverGround ?? 0;

    // observe ngsild-proxy socketIo connection
    if (this.widgetPermissions.ngsildConnectorEnabled) {
      // Query NGSI infos of entity
      if (this.ngsiEntityId?.idPattern) {
        await this.fetchLatestThrusterInfos(this.ngsiEntityId);
      }

      this.listenOnNgsildServiceEvents();
    }

    // Observe setting changes
    this.listenOnWidgetSettingsChanges(this.widgetId);
  }

  private async getWidgetAccessRights(
      user: IUser,
      pluginInjectorService: PluginInjectorService,
      widget: PluginInjectorWidget.IWidget
  ): Promise<void> {
    const appDashboard = user.app_dashboards.find(
        (dashboard: IAppDashboard) => dashboard._id === user.active_dashboard
    );

    if (appDashboard) {
      const pluginPermissions = await pluginInjectorService.permissionService.getPermissions(
          appDashboard,
          user
      );

      const accessRights = pluginPermissions.find(
          (plugin_permission: IPluginPermissions) => plugin_permission.plugin_id === widget?.pluginId
      );

      if (accessRights?.widget_permissions) {
        const modulePermissions = accessRights.widget_permissions[widget.pluginModule.toLowerCase()] as IPermissionsFlags;

        const widgetEnabled = widget?.widgetPermissions?.widgetEnabledPermission?.permissionRef;
        const widgetSettingsEnabled =
            widget?.widgetPermissions?.widgetBasePermissions?.widgetSettingsEnabledPermission?.permissionRef;
        const ngsildConnectorEnabled =
            widget?.widgetPermissions?.widgetAdditionalPermissions?.[this.extraPermissionRefs.ngsildConnectorEnabled]?.permissionRef;

        this.widgetPermissions = {
          widgetEnabled: widgetEnabled ? modulePermissions[widgetEnabled] : false,
          widgetSettingsEditEnabled: widgetSettingsEnabled ? modulePermissions[widgetSettingsEnabled] : false,
          ngsildConnectorEnabled: ngsildConnectorEnabled ? modulePermissions[ngsildConnectorEnabled] : false
        };
      }
    }
  }

  private async fetchLatestThrusterInfos(entityId: Omit<INgsiEntityIdAttrs, "ngsildPrefix"> | undefined): Promise<void> {
    const widgetEntityId = this.getWidgetEntityId(entityId);

    if (widgetEntityId === undefined) {
      return;
    }

    const uuid = await this.ngsildConnectorService.uuidFromSettings();
    const args = {
      uuid: uuid,
      idPattern: widgetEntityId
    };

    this.ngsildConnectorService.queryEntitiesById$(args)
    .pipe(takeUntil(this.ngUnsubscribe$))
    .subscribe((result: INgsiEntity[])=> {
      if (result.length === 0) {
        console.log(`NGSI query has not returned any data.`)
        return;
      }

      const entity = result[0] as INgsiAIS;
      this.updateCompassInfos(entity);
    })
  }

  private listenOnNgsildServiceEvents(): void {
    // Register when the component is called up
    this.registerForNotification(this.widgetId, this.ngsiEntityId);

    // Listen if the plugin is switch from deactivate to activate and widget is on dashboard visible
    this.ngsildConnectorService.socket.on('connect',() => {
      if (this.ngsildConnectorService.socket.ioSocket.connected && this.widgetPermissions.ngsildConnectorEnabled) {
        this.registerForNotification(this.widgetId, this.ngsiEntityId);
      }
    });
  }

  private registerForNotification(widgetId: string | undefined, ngsiEntityId: Omit<INgsiEntityIdAttrs, "ngsildPrefix"> | undefined): void {
    if (!widgetId) {
      console.info('Compass widgetId is not assigned  - registration is aborted');
      return;
    }

    if (!ngsiEntityId || !this.currDashboardId) {
      return;
    }

    this.ngsildConnectorService.registerNotificationHandler(widgetId, this.currDashboardId, <string>this.openBridgeCompassWidget?.class, (notification: INotification) => {
      const isRelevant = this.filterIncomingNgsINotification(
          notification,
          widgetId,
          ngsiEntityId,
          this.widgetSubscriptions
      )

      if (isRelevant) {
        const entity = notification.data[0] as INgsiAIS;
        this.updateCompassInfos(entity);
      }
    });
  }

  private listenOnWidgetSettingsChanges(widgetId: string): void {
    this.compassService.getCompassEntity$(widgetId)
    .pipe(
        takeUntil(this.ngUnsubscribe$),
        filter((entityId: Omit<INgsiEntityIdAttrs, 'ngsildPrefix'> | undefined): entityId is Omit<INgsiEntityIdAttrs, 'ngsildPrefix'> =>
            entityId != undefined &&
            entityId.idPattern != this.ngsiEntityId?.idPattern
        ),
    )
    .subscribe(async (entityId: Omit<INgsiEntityIdAttrs, 'ngsildPrefix'>) => {
      if (!this.widgetId) {
        console.error(`Widget data not correctly loaded.`);
        return;
      }

      this.ngsiEntityId = entityId;
      this.widgetSubscriptions = await this.modelService._ngsiSubscriptionWidget.listAll(`type=${this.entityType}`);
      this.listenOnNgsildServiceEvents();
    })
  }

  private filterIncomingNgsINotification(
      notification: INotification,
      widgetId: string | undefined,
      entityId: Omit<INgsiEntityIdAttrs, "ngsildPrefix"> | undefined,
      widgetSubscriptions: INgsiSubscriptionWidget[]
  ): boolean {
    const entity = notification.data[0] as INgsiAIS;
    const widgetEntityId = this.getWidgetEntityId(entityId);

    const subForWidgetExist = widgetSubscriptions.some((widgetSubscription: INgsiSubscriptionWidget) =>
        widgetSubscription.subscriptionId === notification.subscriptionId &&
        widgetSubscription.app_plugin_widget === widgetId
    );

    const sameEntityId = entity.id === widgetEntityId;

    return (subForWidgetExist && sameEntityId);
  }

  private getWidgetEntityId(entityId: Omit<INgsiEntityIdAttrs, "ngsildPrefix"> | undefined): string | undefined {
    if (!entityId?.idPattern) {
      return undefined;
    }

    return entityId?.owner ?
        `${ModuleEntityTypesConfig.NGSI_ENTITY_ID_PREFIX}${entityId?.type}:${entityId?.owner}:${entityId?.idPattern}` :
        `${ModuleEntityTypesConfig.NGSI_ENTITY_ID_PREFIX}${entityId?.type}:${entityId?.idPattern}`;
  }

  private updateCompassInfos(entity: INgsiAIS): void {
    const heading = entity?.hdg;
    const cog = entity?.cog;

    if (heading) {
      this.obcNavigationsInstrumentsInstance.compass.heading = heading;
    }

    if (cog) {
      this.obcNavigationsInstrumentsInstance.compass.courseOverGround = cog;
    }
  }

  private observeStyleChanges(): void {
    this.resizeObserver = new ResizeObserver(() => {
      this.setWidgetHeaderOnHover(this.isHeaderHovered);
    });

    if (this.obcNavigationInstrumentContainer?.nativeElement) {
      this.resizeObserver.observe(this.obcNavigationInstrumentContainer?.nativeElement)
    }
  }

  private setWidgetHeaderOnHover(isHeaderHovered: boolean): void {
    if (this.obcNavigationInstrumentContainer?.nativeElement) {
      this.obcNavigationInstrumentContainer.nativeElement.style.marginTop = isHeaderHovered ? '35px' : '0';
    }
  }

  ngOnDestroy(): void {
    // Clear to all component subscriptions
    this.ngUnsubscribe$.complete();

    // Clear extern plugins subscriptions
    if (this.widgetId) {
      this.ngsildConnectorService.unregisterNotificationHandler(this.widgetId, <string>this.openBridgeCompassWidget?.class);
      this.compassService.removeCompassEntity(this.widgetId);
    }

    // Clean up ResizeObserver
    this.resizeObserver?.disconnect();
  }
}
